/**
 * Agent-Live-View: kleiner Browser-Client für den SSE-Feed `/agent/live/events`.
 * Zeigt Verbindungsstatus, optionale Filter, einen Kurz-Host-Status aus den
 * Heartbeats und den Live-Event-Stream. KEIN DB-Polling, kein großes Dashboard —
 * der Beweis, dass Browser → SSE → UI stabil läuft. Reine Logik in
 * `../agent-live.ts`, EventSource in `../agent-live-client.ts`.
 */

import { useSignal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import { hasPermission } from "../store";
import { PageHead, Panel } from "../components";
import { t, type TranslationKey } from "../i18n";
import { AgentLiveClient } from "../agent-live-client";
import {
  applyAgentEvent,
  applyBackfill,
  applyHeartbeat,
  applyReady,
  applyStatus,
  buildKillfeedItems,
  buildLiveUrl,
  clampPercent,
  formatAgentEventSummary,
  formatClock,
  formatKillfeedText,
  formatPercent,
  formatUptime,
  getHeartbeatStatus,
  initialAgentLiveState,
  sortHeartbeatsForStatus,
  AGENT_LIVE_TYPE_OPTIONS,
  DASH,
  type AgentLiveState,
  type HeartbeatStatus,
  type KillfeedItem,
  type KillfeedLabels,
  type LiveHeartbeatFrame,
  type LiveStatus,
} from "../agent-live";

const PERMISSION = "view.agent_events";

const HB_STATUS_LABEL: Record<HeartbeatStatus, TranslationKey> = {
  online: "agentLive.statusOnline",
  stale: "agentLive.statusStale",
  offline: "agentLive.statusOffline",
  unknown: "agentLive.statusUnknown",
};

function statusLabel(s: LiveStatus): string {
  switch (s) {
    case "connecting": return t("agentLive.statusConnecting");
    case "connected": return t("agentLive.statusConnected");
    case "error": return t("agentLive.statusError");
    case "closed": return t("agentLive.statusClosed");
  }
}

function statusTone(s: LiveStatus): string {
  if (s === "connected") return "ok";
  if (s === "error") return "down";
  if (s === "connecting") return "warn";
  return "off";
}

function MetricBar({ label, percent }: { label: string; percent: number | null | undefined }) {
  const clamped = clampPercent(percent);
  return (
    <div class="agent-live-metric">
      <span class="alm-label">{label}</span>
      <span class="alm-value">{formatPercent(percent)}</span>
      <div class="agent-live-metric__bar">
        <span class="agent-live-metric__fill" style={`width:${clamped ?? 0}%`} />
      </div>
    </div>
  );
}

function platformLabel(hm: LiveHeartbeatFrame["host_metrics"]): string {
  if (!hm) return DASH;
  const sys = hm.platform_system;
  const rel = hm.platform_release;
  if (!sys && !rel) return DASH;
  return [sys, rel].filter(Boolean).join(" ");
}

function StatusCard({ hb, status }: { hb: LiveHeartbeatFrame; status: HeartbeatStatus }) {
  const hm = hb.host_metrics;
  return (
    <div class={`agent-live-status-card status-${status}`}>
      <div class="agent-live-status-card__head">
        <span class="alsc-server">{hb.server_id}</span>
        <span class={`agent-live-status-badge agent-live-status-badge--${status}`}>{t(HB_STATUS_LABEL[status])}</span>
      </div>
      <div class="agent-live-status-sub">
        <span>{hb.hostname ?? DASH}</span>
        <span class="dot-sep">·</span>
        <span>{t("agentLive.agent")} {hb.agent_version ?? DASH}</span>
        <span class="dot-sep">·</span>
        <span>{platformLabel(hm)}</span>
      </div>

      <div class="agent-live-metrics">
        <MetricBar label={t("agentLive.cpu")} percent={hm?.cpu_percent} />
        <MetricBar label={t("agentLive.ram")} percent={hm?.memory_percent} />
        <MetricBar label={t("agentLive.disk")} percent={hm?.disk_percent} />
      </div>

      <div class="agent-live-status-meta">
        {hm && hm.load_avg_1 != null && (
          <span><i>{t("agentLive.loadAvg")}</i> {hm.load_avg_1}</span>
        )}
        <span><i>{t("agentLive.queue")}</i> {hb.queue_size ?? DASH}</span>
        <span><i>{t("agentLive.lastEvent")}</i> {formatClock(hb.last_event_ts)}</span>
        <span><i>{t("agentLive.lastHeartbeat")}</i> {formatClock(hb.sent_at)}</span>
        <span><i>{t("agentLive.uptime")}</i> {formatUptime(hm?.uptime_seconds)}</span>
      </div>
    </div>
  );
}

function killfeedLabels(): KillfeedLabels {
  return {
    unknownWeapon: t("agentLive.kfUnknownWeapon"),
    suicide: t("agentLive.kfSuicide"),
    environment: t("agentLive.kfEnvironment"),
  };
}

function KillfeedRow({ item }: { item: KillfeedItem }) {
  const labels = killfeedLabels();
  const cls =
    `agent-live-killfeed__item agent-live-killfeed__item--${item.kind}` +
    (item.headshot ? " agent-live-killfeed__item--headshot" : "");
  return (
    <li class={cls}>
      <span class="agent-live-killfeed__time">{formatClock(item.ts)}</span>
      <span class="agent-live-killfeed__server">{item.server_id}</span>
      {item.map && <span class="agent-live-killfeed__map">{item.map}</span>}
      <span class="agent-live-killfeed__text">{formatKillfeedText(item, labels)}</span>
      {item.headshot && <span class="agent-live-killfeed__badge">{t("agentLive.kfHeadshot")}</span>}
      {item.weapon && <span class="agent-live-killfeed__weapon">{item.weapon}</span>}
    </li>
  );
}

export function AgentLive() {
  const state = useSignal<AgentLiveState>(initialAgentLiveState());
  const serverFilter = useSignal("");
  const typeFilter = useSignal(""); // "" = alle
  // Client-Uhr: lässt die Online/Offline-Bewertung der Cards altern, OHNE Daten
  // zu pollen (rein lokale Zeit; die Daten kommen weiter nur per SSE-Push).
  const nowMs = useSignal(Date.now());

  if (!hasPermission(PERMISSION)) {
    return (
      <div class="view-enter agent-live">
        <PageHead title={t("nav.agentLive")} sub={t("agentLive.sub")} />
        <Panel><div class="panel-body empty">{t("agentLive.forbidden")}</div></Panel>
      </div>
    );
  }

  const url = buildLiveUrl({
    serverId: serverFilter.value || null,
    types: typeFilter.value ? [typeFilter.value] : null,
    limit: 100,
  });

  // Verbindungs-Lifecycle: bei Filterwechsel (neue URL) neu verbinden, beim
  // Unmount sauber schließen. Reducer halten den State; kein setInterval-Polling.
  useEffect(() => {
    state.value = initialAgentLiveState();
    const client = new AgentLiveClient(url, {
      onStatus: (s) => { state.value = applyStatus(state.value, s); },
      onReady: (d) => { state.value = applyReady(state.value, d); },
      onBackfill: (evs) => { state.value = applyBackfill(state.value, evs); },
      onEvent: (ev) => { state.value = applyAgentEvent(state.value, ev, Date.now()); },
      onHeartbeat: (hb) => { state.value = applyHeartbeat(state.value, hb); },
    });
    client.connect();
    return () => client.close();
  }, [url]);

  // Status-Cards alle 5 s neu bewerten (90/180 s Schwellen). Kein Datenabruf.
  useEffect(() => {
    const id = setInterval(() => { nowMs.value = Date.now(); }, 5000);
    return () => clearInterval(id);
  }, []);

  const st = state.value;
  const heartbeats = sortHeartbeatsForStatus(Object.values(st.heartbeats), nowMs.value);
  const killfeed = buildKillfeedItems(st.events);

  return (
    <div class="view-enter agent-live">
      <PageHead title={t("nav.agentLive")} sub={t("agentLive.sub")}>
        <div class={`agent-live-status ${statusTone(st.status)}`}>
          <span class="live-dot" />
          <span>{statusLabel(st.status)}</span>
          {st.status === "error" && <span class="ahb-reconnect">· {t("agentLive.reconnecting")}</span>}
        </div>
      </PageHead>

      <Panel
        title={t("agentLive.filters")}
        actions={
          <span class="muted small">
            {t("agentLive.lastEvent")}: {st.lastEventAtMs ? new Date(st.lastEventAtMs).toLocaleTimeString() : DASH}
          </span>
        }
      >
        <div class="agent-live-controls">
          <label>
            <span>{t("agentLive.serverFilter")}</span>
            <input
              type="text"
              value={serverFilter.value}
              placeholder="server_id"
              onInput={(e) => { serverFilter.value = (e.target as HTMLInputElement).value.trim(); }}
            />
          </label>
          <label>
            <span>{t("agentLive.typeFilter")}</span>
            <select value={typeFilter.value} onChange={(e) => { typeFilter.value = (e.target as HTMLSelectElement).value; }}>
              <option value="">{t("agentLive.allTypes")}</option>
              {AGENT_LIVE_TYPE_OPTIONS.map((ty) => (
                <option key={ty} value={ty}>{ty}</option>
              ))}
            </select>
          </label>
          <span class="muted small">{st.eventCount} {t("agentLive.eventsReceived")}</span>
        </div>
      </Panel>

      <Panel title={t("agentLive.serverStatus")}>
        {heartbeats.length === 0 ? (
          <div class="panel-body empty">{t("agentLive.noHeartbeats")}</div>
        ) : (
          <div class="agent-live-status-grid">
            {heartbeats.map((hb) => (
              <StatusCard key={hb.server_id} hb={hb} status={getHeartbeatStatus(hb, nowMs.value)} />
            ))}
          </div>
        )}
      </Panel>

      <Panel title={t("agentLive.killfeed")}>
        {killfeed.length === 0 ? (
          <div class="panel-body empty agent-live-killfeed__empty">{t("agentLive.noKills")}</div>
        ) : (
          <ul class="agent-live-killfeed">
            {killfeed.map((item) => (
              <KillfeedRow key={item.event_id} item={item} />
            ))}
          </ul>
        )}
      </Panel>

      <Panel title={t("agentLive.events")}>
        {st.events.length === 0 ? (
          <div class="panel-body empty">{t("agentLive.noEvents")}</div>
        ) : (
          <ul class="agent-live-events">
            {st.events.map((ev) => (
              <li key={ev.event_id} class="agent-live-event">
                <span class="ale-time">{formatClock(ev.ts)}</span>
                <span class={`ale-type ale-${ev.type}`}>{ev.type}</span>
                <span class="ale-server">{ev.server_id}</span>
                <span class="ale-map">{ev.map || DASH}</span>
                <span class="ale-summary">{formatAgentEventSummary(ev)}</span>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}
